/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Globe, ShieldCheck, Heartbeat, Drop, Buildings,
  Leaf, Recycle, Lightning, ChartLine, MapTrifold, Users,
  Certificate, GraduationCap, Handshake, Target, Sparkle,
  Tree, Fish, FlowerLotus, Factory, ClipboardText, Coins,
} from "@phosphor-icons/react";
import { MarketingNav, MarketingFooter, BTN_GREEN, BTN_DARK } from "./_shared";

/* ============================================================
 * /sdg2030 — Accelerating SDG 2030 through Smart Waste,
 * Public Health & Sustainable Governance.
 * ============================================================ */

// Official SDG palette (un.org brand spec)
const SDGS = [
  {
    n: 3,  color: "#4C9F38",
    title: "Good Health & Well-Being",
    Icon: Heartbeat,
    levers: ["Biomedical waste safety", "Household sanitary waste systems",
              "Disease prevention", "Women & child sanitation", "Public hygiene"],
    kpi: "12,400+ households served · 96% disease-vector reduction",
  },
  {
    n: 6,  color: "#26BDE2",
    title: "Clean Water & Sanitation",
    Icon: Drop,
    levers: ["Safe waste segregation at source", "Community sanitation",
              "Waste leakage reduction", "Pollution prevention"],
    kpi: "0.4% leakage rate · 100% chain-of-custody traceability",
  },
  {
    n: 9,  color: "#FD6925",
    title: "Industry, Innovation & Infrastructure",
    Icon: Factory,
    levers: ["Smart waste SaaS", "QR-based logistics", "Route optimization",
              "Waste infrastructure modernization"],
    kpi: "23% fuel saved · 41% pickup-time reduction",
  },
  {
    n: 11, color: "#FD9D24",
    title: "Sustainable Cities & Communities",
    Icon: Buildings,
    levers: ["Smart municipalities", "Health zones", "Waste intelligence",
              "Safer urban sanitation"],
    kpi: "14 LSGDs onboarded · 4 states · CPCB-aligned dashboards",
  },
  {
    n: 12, color: "#BF8B2E",
    title: "Responsible Consumption & Production",
    Icon: Recycle,
    levers: ["Waste reduction at source", "Segregation at intake",
              "Circular-economy routing", "Recycling intelligence"],
    kpi: "62% diversion from landfill · weight-based pricing",
  },
  {
    n: 13, color: "#3F7E44",
    title: "Climate Action",
    Icon: Tree,
    levers: ["Carbon-aware route ordering", "Sustainable logistics",
              "Waste-to-energy diversion", "ESG metrics"],
    kpi: "1,280 tCO₂e avoided · live carbon dashboard",
  },
  {
    n: 14, color: "#0A97D9",
    title: "Life Below Water",
    Icon: Fish,
    levers: ["Pollution-source tracking", "Safe disposal pathways",
              "Coastal LSGD coverage"],
    kpi: "92% reduction in mismanaged waste reaching water bodies",
  },
  {
    n: 15, color: "#56C02B",
    title: "Life on Land",
    Icon: FlowerLotus,
    levers: ["Sanctioned-disposal-only routing", "Leakage prevention",
              "Soil-contaminant safeguards"],
    kpi: "Zero illegal-dump events across operating LSGDs (FY26 YTD)",
  },
];

const LSGD_MODULES = [
  { Icon: MapTrifold,    title: "Health Zones",        desc: "Geofenced sanitation areas with auto-assignment of pickup teams." },
  { Icon: ChartLine,     title: "Route Scheduling",    desc: "Compliance-driven daily routes with carbon and fuel telemetry." },
  { Icon: Users,         title: "Citizen Registration", desc: "QR onboarding for households, institutions & street vendors." },
  { Icon: ShieldCheck,   title: "Compliance Dashboards", desc: "BMW Rules 2016 + CPCB-aligned audit trail with exports." },
  { Icon: Heartbeat,     title: "Public-Health Sanitation", desc: "Vector control + biomedical safety in one operating system." },
  { Icon: Certificate,   title: "Certificate Systems", desc: "Court-defensible disposal certificates with cert-no verification." },
  { Icon: GraduationCap, title: "Training Academy",    desc: "BMW & sanitation curricula, exam-gated completion, partner-co-branding." },
];

const GOVERNANCE_KPIS = [
  { v: "1.4M", l: "Citizens covered",     accent: "#0B2B66" },
  { v: "62%",  l: "Diversion from landfill", accent: "#2D5A27" },
  { v: "1.28k", l: "tCO₂e avoided (YTD)", accent: "#3F7E44" },
  { v: "94%",  l: "Source-segregation rate", accent: "#C9A227" },
  { v: "<0.4%", l: "Leakage rate",        accent: "#26BDE2" },
  { v: "100%", l: "Chain-of-custody",     accent: "#B12A1E" },
];

const POLICY = [
  { t: "LSGD Digitalization Mission",  d: "Aligned to Kerala / state LSGD modernization mandates." },
  { t: "SDG Localization Framework",   d: "Maps every operating dataset to NITI Aayog's SDG India Index indicators." },
  { t: "Swachh Bharat 2.0",            d: "Source-segregation + waste-to-wealth contributions tracked at LSGD level." },
  { t: "Public Health Infrastructure", d: "BMW Rules 2016 compliance baked into the chain of custody." },
];

export default function SDG2030() {
  useEffect(() => {
    document.title = "SDG 2030 · Smart Waste & Sustainable Governance · Rescigo Health";
  }, []);
  return (
    <div className="min-h-screen bg-[#fafaf6]" data-testid="page-sdg2030">
      <MarketingNav/>

      {/* ========== HERO ========== */}
      <section className="relative overflow-hidden pt-32 pb-24 px-6 lg:px-12">
        {/* Background gradient + accent rings */}
        <div className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 12% 20%, rgba(76,159,56,0.18), transparent 45%)," +
                  "radial-gradient(circle at 88% 78%, rgba(38,189,226,0.18), transparent 50%)," +
                  "linear-gradient(180deg, #f5f8f3 0%, #fafaf6 60%)",
              }}/>
        {Array.from({ length: 24 }).map((_, i) => (
          <motion.div key={i}
            className="absolute w-1 h-1 rounded-full bg-emerald-400/50"
            style={{ left: `${(i * 41) % 100}%`, top: `${(i * 67) % 100}%` }}
            animate={{ opacity: [0, 0.7, 0], y: [0, -30, -60] }}
            transition={{ duration: 5 + (i % 4), delay: (i % 6) * 0.3,
                          repeat: Infinity, ease: "easeOut" }}/>
        ))}
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-700/8 border border-emerald-700/25 text-emerald-800 text-[10px] uppercase tracking-[0.22em] font-semibold mb-7">
              <Globe size={11} weight="duotone"/> SDG 2030 · UN Localization
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05]">
              Accelerating <span className="text-emerald-700">SDG 2030</span> through smart waste, public health & sustainable governance.
            </h1>
            <p className="text-lg text-slate-600 mt-6 max-w-2xl leading-relaxed">
              Empowering LSGDs, state governments and public institutions with
              <strong className="text-slate-800"> actionable waste intelligence</strong>,
              compliance systems, sanitation innovation, and measurable
              sustainability outcomes.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/contact" data-testid="sdg-cta-gov" className={BTN_GREEN}>
                Talk to a Governance Lead <ArrowRight size={13}/>
              </Link>
              <Link to="/for-government" data-testid="sdg-cta-explore" className={BTN_DARK}>
                Explore Government Solutions
              </Link>
            </div>
            <div className="mt-9 flex flex-wrap gap-2 text-[11px] text-slate-500">
              <Tag label="CPCB-aligned"/>
              <Tag label="BMW Rules 2016"/>
              <Tag label="ISO 27001"/>
              <Tag label="NITI Aayog SDG-India"/>
            </div>
          </div>
          {/* Hero KPI cluster */}
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              {GOVERNANCE_KPIS.slice(0, 4).map(k => (
                <KpiTile key={k.l} v={k.v} l={k.l} accent={k.accent}/>
              ))}
            </div>
            <div className="mt-3 p-5 rounded-xl bg-white border border-slate-200/80 flex items-center gap-3">
              <Sparkle size={22} weight="duotone" className="text-amber-500 flex-none"/>
              <div className="text-xs text-slate-600 leading-relaxed">
                Every operational dataset maps to a NITI Aayog SDG India Index indicator —
                ready for state-level reporting.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SDG IMPACT MAP ========== */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            kicker="SDG Impact Map"
            title="Eight SDGs. One operating system."
            sub="Where Rescigo directly contributes — and how each contribution is measured." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12"
                data-testid="sdg-grid">
            {SDGS.map(s => (
              <SdgCard key={s.n} s={s}/>
            ))}
          </div>
        </div>
      </section>

      {/* ========== LSGD SOLUTIONS ========== */}
      <section className="py-24 px-6 lg:px-12 bg-[#fafaf6]">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            kicker="LSGD Solutions"
            title="A modular sanitation operating system."
            sub="Deployable per ward, panchayat, municipality or corporation — scaling up to a unified state dashboard." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12"
                data-testid="lsgd-modules">
            {LSGD_MODULES.map(m => (
              <ModuleCard key={m.title} {...m}/>
            ))}
          </div>
        </div>
      </section>

      {/* ========== MEASURABLE GOVERNANCE ========== */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            kicker="Measurable Governance"
            title="Numbers that survive an audit."
            sub="Live, ward-level performance indicators — exportable as compliance reports." />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-12"
                data-testid="governance-kpis">
            {GOVERNANCE_KPIS.map(k => (
              <KpiTile key={k.l} v={k.v} l={k.l} accent={k.accent} large/>
            ))}
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-4">
            <ImpactBlock Icon={Lightning} title="Carbon Impact"
              desc="Live tCO₂e telemetry across pickup, transport and disposal — exportable for ESG filings."/>
            <ImpactBlock Icon={Recycle} title="Circularity Score"
              desc="Diversion-from-landfill ratio per LSGD with month-over-month comparison."/>
            <ImpactBlock Icon={Coins} title="CSR & Funding Hooks"
              desc="Earmarked impact ledgers for corporate CSR contributions to LSGD initiatives."/>
          </div>
        </div>
      </section>

      {/* ========== POLICY SUPPORT ========== */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-[#0A2540] via-[#0B2B66] to-[#0A2540] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)," +
                  "linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                backgroundSize: "44px 44px",
              }}/>
        <div className="relative max-w-7xl mx-auto">
          <SectionHeader
            invert
            kicker="Policy Alignment"
            title="Built for India's public-health digital stack."
            sub="Aligned to the frameworks that already shape how LSGDs report, fund and scale." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {POLICY.map(p => (
              <div key={p.t} className="rounded-xl border border-white/15 bg-white/5 backdrop-blur p-5">
                <ClipboardText size={18} weight="duotone" className="text-emerald-300"/>
                <div className="font-heading mt-3 text-base">{p.t}</div>
                <div className="text-sm text-white/70 mt-2 leading-relaxed">{p.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== CASE FOR GOVERNMENTS ========== */}
      <section className="py-28 px-6 lg:px-12 bg-[#fafaf6]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-700/8 border border-emerald-700/25 text-emerald-800 text-[10px] uppercase tracking-[0.22em] font-semibold mb-7">
            <Target size={11} weight="duotone"/> The Case for Governments
          </div>
          <h2 className="font-heading text-4xl md:text-5xl text-slate-900 leading-[1.1]">
            Transform your municipality into a measurable
            <span className="text-emerald-700"> SDG-driven</span> sanitation and sustainability ecosystem.
          </h2>
          <p className="text-lg text-slate-600 mt-6 max-w-2xl mx-auto leading-relaxed">
            Replace fragmented spreadsheets with a unified, court-defensible operating system.
            Quantify every kilogram, every kilometre, every certificate — and report it back to citizens.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact" data-testid="sdg-cta-pilot" className={BTN_GREEN}>
              Request a pilot proposal <ArrowRight size={13}/>
            </Link>
            <Link to="/white-papers" className={BTN_DARK}>
              Read our framework white paper
            </Link>
          </div>
        </div>
      </section>

      {/* ========== CSR + PRIVATE SECTOR ========== */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeader
              align="left"
              kicker="CSR & Private Sector"
              title="Corporates can fund the public-health backbone."
              sub="Direct CSR rupees into measurable LSGD outcomes — tracked end-to-end with attribution receipts." />
            <ul className="mt-8 space-y-3">
              {[
                ["CSR programs",                "Earmarked funding mapped to specific LSGD wards & SDG outcomes."],
                ["Public sanitation initiatives", "Underwrite biomedical & household sanitation rollouts."],
                ["Waste infrastructure funding", "Co-invest in route fleets, transfer stations, weighing equipment."],
              ].map(([t, d]) => (
                <li key={t} className="flex gap-3">
                  <Handshake size={20} weight="duotone" className="text-emerald-700 flex-none mt-0.5"/>
                  <div>
                    <div className="font-semibold text-slate-900">{t}</div>
                    <div className="text-sm text-slate-600 leading-relaxed">{d}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-9">
              <Link to="/csr-sustainability" data-testid="sdg-cta-csr" className={BTN_GREEN}>
                CSR partnership pathways <ArrowRight size={13}/>
              </Link>
            </div>
          </div>
          {/* Visual: stacked impact ledger */}
          <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white p-8 border border-emerald-200/50">
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-800 font-semibold">CSR Impact Ledger · sample</div>
            <div className="mt-4 space-y-3">
              {[
                { kpi: "₹ 24L deployed",        out: "3 wards onboarded", color: "#2D5A27" },
                { kpi: "92t waste diverted",     out: "62 tCO₂e avoided",  color: "#3F7E44" },
                { kpi: "1,180 households",       out: "covered by sanitary pickups", color: "#0B2B66" },
                { kpi: "440 trainees certified", out: "across 7 institutions", color: "#C9A227" },
              ].map((r, i) => (
                <div key={i} className="flex items-center justify-between p-4 rounded-lg bg-white border border-emerald-200/40">
                  <div>
                    <div className="font-semibold" style={{ color: r.color }}>{r.kpi}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{r.out}</div>
                  </div>
                  <Leaf size={16} weight="duotone" style={{ color: r.color }}/>
                </div>
              ))}
            </div>
            <div className="mt-5 text-[11px] text-slate-500 italic">
              Live ledgers are auto-generated from your funded LSGDs.
            </div>
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-24 px-6 lg:px-12 bg-emerald-700 text-white relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-[400px] h-[400px] rounded-full bg-emerald-400/20 blur-3xl pointer-events-none"/>
        <div className="absolute -left-20 -bottom-20 w-[400px] h-[400px] rounded-full bg-white/10 blur-3xl pointer-events-none"/>
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-4xl md:text-5xl leading-[1.1]">
            Make your next decade count.
          </h2>
          <p className="text-base md:text-lg text-white/85 mt-6 max-w-2xl mx-auto leading-relaxed">
            Localize SDG 2030 with infrastructure that ships in weeks, not years.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact" data-testid="sdg-cta-final"
                  className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-white text-emerald-800 hover:bg-emerald-50 transition text-sm font-semibold">
              Book a 30-minute briefing <ArrowRight size={13}/>
            </Link>
            <Link to="/training" className="inline-flex items-center gap-2 h-12 px-7 rounded-full border border-white/30 hover:border-white/60 hover:bg-white/5 text-sm font-medium">
              Train my LSGD team
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter/>
    </div>
  );
}

/* ============================================================
 * Reusable bits
 * ============================================================ */
function SectionHeader({ kicker, title, sub, align = "center", invert = false }) {
  const text = invert ? "text-white" : "text-slate-900";
  const subColor = invert ? "text-white/70" : "text-slate-600";
  const kickerColor = invert ? "text-emerald-300" : "text-emerald-700";
  return (
    <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
      <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${kickerColor}`}>{kicker}</div>
      <h2 className={`font-heading text-3xl md:text-4xl mt-3 leading-[1.15] ${text}`}>{title}</h2>
      {sub && <p className={`mt-4 text-base md:text-lg leading-relaxed ${subColor}`}>{sub}</p>}
    </div>
  );
}

function Tag({ label }) {
  return (
    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-600 font-medium">
      <span className="w-1 h-1 rounded-full bg-emerald-500"/> {label}
    </span>
  );
}

function KpiTile({ v, l, accent, large = false }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-4 relative overflow-hidden">
      <div className="absolute -right-3 -top-3 w-14 h-14 rounded-full opacity-[0.08]"
            style={{ background: accent }}/>
      <div className={`font-heading ${large ? "text-3xl" : "text-2xl"}`}
            style={{ color: accent }}>{v}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">{l}</div>
    </div>
  );
}

function SdgCard({ s }) {
  const Icon = s.Icon;
  return (
    <motion.div
      whileHover={{ y: -3 }}
      className="rounded-xl border bg-white overflow-hidden flex flex-col"
      style={{ borderColor: `${s.color}33` }}
      data-testid={`sdg-card-${s.n}`}>
      <div className="px-5 pt-5 pb-4 flex items-start gap-3"
            style={{ background: `${s.color}0F` }}>
        <div className="w-12 h-12 rounded-lg grid place-items-center flex-none font-heading text-lg text-white"
              style={{ background: s.color }}>
          {s.n}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold"
                style={{ color: s.color }}>SDG {s.n}</div>
          <div className="font-semibold text-slate-900 mt-0.5 leading-tight">{s.title}</div>
        </div>
        <Icon size={18} weight="duotone" className="ml-auto flex-none"
              style={{ color: s.color }}/>
      </div>
      <div className="px-5 py-4 flex-1">
        <ul className="space-y-1.5 text-xs text-slate-600">
          {s.levers.map(l => (
            <li key={l} className="flex gap-2">
              <span className="mt-1 w-1 h-1 rounded-full flex-none"
                    style={{ background: s.color }}/>
              {l}
            </li>
          ))}
        </ul>
      </div>
      <div className="px-5 py-3 border-t border-slate-100 text-[11px] font-mono"
            style={{ color: s.color }}>
        {s.kpi}
      </div>
    </motion.div>
  );
}

function ModuleCard({ Icon, title, desc }) {
  return (
    <div className="rounded-xl border border-slate-200/80 bg-white p-5 hover:border-emerald-300 hover:shadow-md transition">
      <div className="w-10 h-10 rounded-lg bg-emerald-700/8 grid place-items-center text-emerald-700">
        <Icon size={18} weight="duotone"/>
      </div>
      <div className="font-heading mt-4 text-base text-slate-900">{title}</div>
      <div className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</div>
    </div>
  );
}

function ImpactBlock({ Icon, title, desc }) {
  return (
    <div className="p-5 rounded-xl bg-gradient-to-br from-slate-50 to-white border border-slate-200/60">
      <Icon size={20} weight="duotone" className="text-emerald-700"/>
      <div className="font-heading mt-3 text-base text-slate-900">{title}</div>
      <div className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</div>
    </div>
  );
}
